import { Column, useRowSelection } from 'react-data-grid';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import type { Data, Fields, Meta, SelectOption } from '../types';
import { MULTI_VALUE_SEPARATOR } from '../types';
import { FaInfoCircle, FaExclamationTriangle, FaChevronDown, FaChevronRight, FaPaperclip, FaTimes } from 'react-icons/fa';
import { MatchColumnSelect } from './MatchColumnSelect';
import BasicTooltip from 'common/components/basicTooltip/BasicTooltip';
import Select from 'common/components/select/Select';
import DateTimePicker from 'common/components/dateTimePicker/DateTimePicker';
import { Portal } from 'react-overlays';
// moment (e não dayjs) porque o parse estrito por formato precisa do customParseFormat, que a app
// não regista no dayjs — é também o que o Utils.parseToDate usa.
import moment from 'moment';
import { DATE_FORMAT_DEFAUT, DATEPICKER_FORMAT_DEFAULT } from 'Config';

import CheckBox from 'common/components/checkBox/CheckBox';
import toggleSwitchStyle from 'common/components/toggleSwitch/ToggleSwitch.module.scss'
const SELECT_COLUMN_KEY = 'select-row';

// Task #66347: o calendário sai para o portal que a aplicação já usa nos date pickers — dentro da
// célula ficaria cortado pelo overflow do react-data-grid.
const CalendarPortal = ({ children }: any) => <Portal container={document.getElementById('calendar-portal')}>{children}</Portal>;

// Task #66347: quando os avisos da validação assíncrona mudam, o passo reconstrói as colunas e o
// react-data-grid desenha a célula com um formatter novo — para o React é outro componente, logo
// remonta e o estado interno perde-se. No multi-valor isso fechava o menu a meio de uma escolha
// múltipla, obrigando a reabrir a cada item. O "está aberto" vive por isso fora da célula, por
// linha+coluna, e é reposto na montagem seguinte.
const openMultiValueCells = new Set<string>();

type MultiValueCellProps = {
  cellKey: string;
  options: SelectOption[];
  value: SelectOption[];
  isDisabled?: boolean;
  maxVisibleTags?: number;
  onChange: (values: SelectOption[]) => void;
};

const MultiValueCell = ({ cellKey, options, value, isDisabled, maxVisibleTags, onChange }: MultiValueCellProps) => {
  const [wasOpen] = useState(() => openMultiValueCells.has(cellKey));

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}
      onClick={event => event.stopPropagation()}
    >
      <Select
        isMulti
        slim
        flat
        maxVisibleTags={maxVisibleTags}
        usePortal
        isDisabled={isDisabled}
        options={options}
        value={value}
        defaultMenuIsOpen={wasOpen}
        onOpenChange={(isOpen: boolean) => {
          if (isOpen) {
            openMultiValueCells.add(cellKey);
          } else {
            openMultiValueCells.delete(cellKey);
          }
        }}
        onChange={(values: any) => onChange((values ?? []) as SelectOption[])}
      />
    </div>
  );
};

// Task #66078: showRowNumber prepends an opt-in "#" column; getRowLine resolves a row's 1-based order
// and getRowWarnings its inline warning messages (both driven by the async validation in the step).
export const generateColumns = <T extends string>(fields: Fields<T>, unmappedKeys?: Set<T>, unmappedTooltip?: string, dateFormats?: Map<string, string>, dateFormatTooltipFn?: (format: string) => string, isCellEditable?: (row: Data<T> & Meta, fieldKey: T) => boolean, getCellLockedReason?: (row: Data<T> & Meta, fieldKey: T) => string | undefined, showRowNumber?: boolean, getRowLine?: (row: Data<T> & Meta) => number | undefined, getRowWarnings?: (row: Data<T> & Meta) => string[] | undefined, getRowIsInfo?: (row: Data<T> & Meta) => boolean, isRowExpanded?: (row: Data<T> & Meta) => boolean, onToggleRowExpanded?: (row: Data<T> & Meta) => void, onUploadFile?: (file: File) => Promise<{ id: string; name: string } | null>, getFileName?: (id: string) => string | undefined, attachFileLabel?: string, removeFileLabel?: string): Column<Data<T> & Meta>[] => [
  ...(showRowNumber ? [{
    key: '__row_number',
    name: '#',
    width: 68,
    minWidth: 56,
    maxWidth: 84,
    resizable: false,
    sortable: false,
    frozen: true,
    formatter: (props: { row: Data<T> & Meta }) => {
      const line = getRowLine?.(props.row);
      const warnings = getRowWarnings?.(props.row);
      const hasWarnings = !!(warnings && warnings.length > 0);
      // Task #66347: informação (ex: "este contrato já existe") não leva o triângulo de aviso — só
      // confundia com erro.
      const isInfo = hasWarnings && !!getRowIsInfo?.(props.row);
      // Native title tooltip + always-rendered icon: robust to react-data-grid re-rendering the
      // selected cell (the BasicTooltip wrapper made the icon flicker/disappear on click).
      return (
        <div
          title={hasWarnings ? warnings!.join(' • ') : undefined}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, height: '100%', color: 'rgba(0, 0, 0, 0.6)' }}
        >
          {onToggleRowExpanded && (
            // Task #66347: expandir a linha dá altura para ver todas as etiquetas de uma célula
            // multi-valor, que fechada mostra só a primeira e um "+N".
            <span
              style={{ cursor: 'pointer', display: 'inline-flex', flexShrink: 0 }}
              onClick={event => { event.stopPropagation(); onToggleRowExpanded(props.row); }}
            >
              {isRowExpanded?.(props.row) ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
            </span>
          )}
          <span>{line ?? ''}</span>
          {hasWarnings
            ? (isInfo
              ? <FaInfoCircle style={{ color: '#0CB6D1', flexShrink: 0 }} />
              : <FaExclamationTriangle style={{ color: '#E6A100', flexShrink: 0 }} />)
            : <></>}
        </div>
      );
    },
  } as Column<Data<T> & Meta>] : []),
  {
    key: SELECT_COLUMN_KEY,
    name: '',
    width: 50,
    minWidth: 50,
    maxWidth: 50,
    resizable: false,
    sortable: false,
    frozen: true,
    formatter: props => {
      const [isRowSelected, onRowSelectionChange] = useRowSelection();
      return (
        <div style={{ paddingTop: 5 }}>
        <CheckBox
        checked={isRowSelected}             
        onChange={event => {
          onRowSelectionChange({
            row: props.row,
            checked: Boolean(event.target.checked),
            isShiftClick: (event.nativeEvent as MouseEvent).shiftKey,
          });
        }}/>
        </div>        
      );
    },
  },
  ...fields.map(
    (column): Column<Data<T> & Meta> => {
    // Tipos cujo conteúdo é desenhado (e clicado) no formatter, sem passar pelo editor do grid.
    const isFormatterDriven = column.fieldType.type === 'checkbox'
      || column.fieldType.type === 'multiselect'
      || column.fieldType.type === 'file';

    return {
      key: column.key,
      name: column.label,
      minWidth: 150,
      resizable: true,
      headerRenderer: () => {
        const isUnmapped = unmappedKeys?.has(column.key as T);
        const dateFormat = dateFormats?.get(column.key);
        const dateTooltip = dateFormat && dateFormatTooltipFn ? dateFormatTooltipFn(dateFormat) : undefined;
        return (
          <div style={{ display:'flex', gap:4, alignItems:'center', position:'relative' }}>
            <div style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis' }}>
              {column.label}
            </div>
            {dateTooltip && (
              <span data-tip={dateTooltip} data-for="validation-unmapped-tooltip" style={{ display: 'inline-flex', flexShrink: 0, cursor: 'pointer' }}>
                <FaInfoCircle />
              </span>
            )}
            {!dateTooltip && isUnmapped && (
              <span data-tip={unmappedTooltip} data-for="validation-unmapped-tooltip" style={{ display: 'inline-flex', flexShrink: 0, cursor: 'pointer' }}>
                <FaInfoCircle />
              </span>
            )}
            {!dateTooltip && !isUnmapped && column.description && (
              <span data-tip={column.description} data-for="validation-unmapped-tooltip" style={{ display: 'inline-flex', flexShrink: 0, cursor: 'pointer' }}>
                <FaInfoCircle />
              </span>
            )}
          </div>
        );
      },
      // Task #66347: o checkbox, o multiselect e o ficheiro desenham-se sempre no formatter e tratam
      // do próprio clique — não abrem editor. Mas continuam `editable` para o grid, porque o
      // react-data-grid só propaga com o quadradinho (fill handle) o que considera editável:
      // `column.editor != null && column.editable !== false`. Marcá-las como não editáveis era o que
      // impedia arrastar um ficheiro ou um toggle para as linhas de baixo.
      editable: isCellEditable ? ((row: Data<T> & Meta) => isCellEditable(row, column.key as T)) : true,
      editor: ({ row, onRowChange, onClose }) => {
        // Existe para o grid as dar como editáveis; quem desenha e trata do clique é o formatter.
        if (isFormatterDriven) {
          return <></>;
        }

        let component;
        //export type Data<T extends string> = { [key in T]: string | boolean | undefined }
        const customStyles = {
            control: (provided: any, state: any) => ({
              ...provided,
              border: 'none',
              borderRadius: 0,
              boxShadow: 'none',
              minHeight: '100%',
              outline: state.isFocused ? '2px solid #1781A1' : 'none',
            }),
            menu: (provided: any) => ({ ...provided, zIndex: 9999, top: 'auto' }),
            valueContainer: (provided: any) => ({ ...provided, padding: '0 8px' }),
            indicatorSeparator: () => ({ display: 'none' }),
        }
        
        switch (column.fieldType.type) {
          case 'date': {
            const valueFormat = column.fieldType.valueFormat || DATE_FORMAT_DEFAUT;
            const current = moment(row[column.key as T] as string, valueFormat, true);
            component = (
              <DateTimePicker
                autoFocus
                selected={current.isValid() ? current.toDate() : null}
                dateFormat={DATEPICKER_FORMAT_DEFAULT}
                popperContainer={CalendarPortal}
                popperProps={{ strategy: 'fixed' } as any}
                onChange={date => onRowChange({
                  ...row,
                  [column.key]: date ? moment(date as Date).format(valueFormat) : '',
                }, true)}
                onClickOutside={() => onClose(true)}
              />
            );
            break;
          }
          case 'select':
            component = (
              <MatchColumnSelect
                value={column.fieldType.options.find(option => option.value === (row[column.key as T] as string))}
                onChange={value => {
                  onRowChange({ ...row, [column.key]: value?.value }, true);
                }}
                options={column.fieldType.options}
                styles={customStyles}
              />
            );
            break;
          default:
            component = (
              <input
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  padding: '0 8px',
                  fontSize: '0.875rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                autoFocus
                value={row[column.key as T] as string}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  onRowChange({ ...row, [column.key]: event.target.value });
                }}
                onFocus={e => { e.target.style.outline = '2px solid #1781A1'; }}
                onBlur={e => { e.target.style.outline = 'none'; onClose(true); }}
              />
            );
        }

        return component;
      },
      editorOptions: {
        // Nas células desenhadas no formatter o clique é delas; o editor (vazio) nunca chega a abrir.
        editOnClick: !isFormatterDriven,
        renderFormatter: isFormatterDriven,
        // Task #66347: o calendário é desenhado fora da célula (portal) — com o fecho por clique
        // fora, escolher um dia fechava o editor antes de o clique chegar ao calendário.
        commitOnOutsideClick: column.fieldType.type !== 'date',
      },
      formatter: ({ row, onRowChange }) => {
        let component;

        const cellLocked = isCellEditable && !isCellEditable(row, column.key as T);
        if (cellLocked && column.fieldType.type !== 'checkbox' && column.fieldType.type !== 'multiselect' && column.fieldType.type !== 'file') {
          const reason = getCellLockedReason?.(row, column.key as T);
          return (
            <div
              style={{ width: '100%', height: '100%', cursor: 'not-allowed' }}
              data-tip={reason || ''}
              data-for="validation-unmapped-tooltip"
            />
          );
        }

        switch (column.fieldType.type) {
          case 'checkbox':
            component = (
              <div style={{ display:'flex', alignItems:'center', height: '100%' }}
                onClick={event => {
                  event.stopPropagation();
                }}
              >               
                <label
                    className={`${toggleSwitchStyle.toggleSwitch} ${toggleSwitchStyle.enabled}`}>
                    <input
                        type='checkbox'                
                        checked={row[column.key as T] as boolean}
                        onChange={() => {
                        onRowChange({ ...row, [column.key]: !row[column.key as T] });
                  }}       
                    />
                    <span className={toggleSwitchStyle.switch}></span>
                </label>
              </div>
            );
            break;
          case 'select':
            component = (
              <div style={{ minWidth:'100%', minHeight:'100%', overflow:'hidden', textOverflow:'ellipsis' }}>
                {column.fieldType.options.find(option => option.value === row[column.key as T])?.label || null}
              </div>
            );
            break;
          case 'file': {
            // Task #66347: a célula guarda a referência do ficheiro (id), nunca o ficheiro. Escolher
            // um faz o upload na hora; a partir daí copiar a linha é copiar a referência.
            const mediaId = (row[column.key as T] as string) || '';
            const fileName = mediaId ? (getFileName?.(mediaId) ?? mediaId) : '';
            const inputId = `rdg-file-${column.key}-${row.__index}`;

            component = (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: '100%', overflow: 'hidden' }}
                onClick={event => event.stopPropagation()}
              >
                {mediaId ? (
                  <>
                    <span title={fileName} style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {fileName}
                    </span>
                    {!cellLocked && (
                      <span
                        title={removeFileLabel}
                        style={{ cursor: 'pointer', display: 'inline-flex', flexShrink: 0, color: '#e04f5f' }}
                        onClick={() => onRowChange({ ...row, [column.key]: '' })}
                      >
                        <FaTimes size={11} />
                      </span>
                    )}
                  </>
                ) : (
                  !cellLocked && onUploadFile && (
                    <label htmlFor={inputId} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, color: '#1781A1', margin: 0 }}>
                      <FaPaperclip size={11} />
                      <span style={{ fontSize: '0.8rem' }}>{attachFileLabel}</span>
                    </label>
                  )
                )}
                {!cellLocked && onUploadFile && (
                  <input
                    id={inputId}
                    type="file"
                    accept={column.fieldType.type === 'file' ? column.fieldType.accept : undefined}
                    style={{ display: 'none' }}
                    onChange={async event => {
                      const chosen = event.target.files?.[0];
                      // Limpa o input para escolher o mesmo ficheiro outra vez voltar a disparar.
                      event.target.value = '';
                      if (!chosen) return;
                      const uploaded = await onUploadFile(chosen);
                      if (uploaded) onRowChange({ ...row, [column.key]: uploaded.id });
                    }}
                  />
                )}
              </div>
            );
            break;
          }
          case 'multiselect': {
            // Task #66347: o valor vive na célula como texto separado por `;` — continua a ser colável
            // do Excel e a passar pelas validações genéricas sem casos especiais.
            const options = column.fieldType.options as SelectOption[];
            const selectedValues = ((row[column.key as T] as string) || '').split(MULTI_VALUE_SEPARATOR)
              .map(part => part.trim()).filter(Boolean);
            const selected = selectedValues.map(value =>
              options.find(option => option.value === value) ?? { label: value, value });
            component = (
              <MultiValueCell
                cellKey={`${row.__index}|${column.key}`}
                options={options}
                value={selected}
                isDisabled={cellLocked}
                maxVisibleTags={isRowExpanded?.(row) ? undefined : 1}
                onChange={values => onRowChange({
                  ...row,
                  [column.key]: values.map(option => option.value).join(MULTI_VALUE_SEPARATOR),
                })}
              />
            );
            break;
          }
          default:
            component = (
              <div style={{ minWidth:'100%', minHeight:'100%', overflow:'hidden', textOverflow:'ellipsis' }}>              
                {row[column.key as T]}
              </div>
            );
        }

        if (row.__errors?.[column.key]) {
          return (
              <BasicTooltip content={row.__errors?.[column.key]?.message}>
                  {component}
              </BasicTooltip>
          );
        }

        return component;
      },
      cellClass: (row: Data<T> & Meta) => {
        const classes: string[] = [];

        if (isCellEditable && !isCellEditable(row, column.key as T) && column.fieldType.type !== 'checkbox') {
          classes.push('rdg-cell-locked');
        }

        switch (row.__errors?.[column.key]?.level) {
          case 'error':
            classes.push('rdg-cell-error');
            break;
          case 'info':
            classes.push('rdg-cell-info');
            break;
        }

        return classes.join(' ');
      },
    };
  }),
];
